<template>
  <div class="punch-report">
    <!-- Employee timesheet -->
    <div v-if="timesheetWorkingData.employee">
      <div class="header">
        <div class="title-block">
          <div>{{ $t('timesheet.employee_card_title').replace('[EMPLOYEE]', fullName(timesheetWorkingData.employee)) }}</div>
          <div>{{ $t('timesheet.employee_card_subtitle') }}</div>
        </div>
      </div>
      <TimesheetEmployee
          v-model="timesheetWorkingData.entries"
          :employee="timesheetWorkingData.employee"
          :company-id="companyId"
          :period-min-date="data.start"
          :period-max-date="data.end"
      />
      <div class="footer">
        <div @click="timesheetQuit" class="btn btn-secondary"><b>‹</b> {{$t('timesheet.back_to_overview')}}</div>
        <div>
          <button @click="timesheetSaveQuit" class="btn btn-secondary">{{$t('save_and_quit')}}</button>
          <button @click="timesheetSave" class="btn btn-primary">{{$t('save')}}</button>
        </div>
      </div>
    </div>

    <!-- Overview -->
    <div v-else>
      <div class="header">
        <div class="title-block">
          <div>{{ $t('timesheet.overview_title') }}</div>
          <div>{{ $t('timesheet.overview_subtitle') }}</div>
        </div>
        <div class="parameters">
          <div>
            <label>{{$t('start')}}</label>
            <datetime
                v-model="parameters.start"
                :max-datetime="parameters.end"
                type="date" value-zone="local"
                input-id="p_start" input-style="height: 32px; width:200px"
            />
          </div>
          <div>
            <label>{{$t('end')}}</label>
            <datetime
                v-model="parameters.end"
                :min-datetime="parameters.start"
                type="date" value-zone="local"
                input-id="p_end" input-style="height: 32px; width:200px"
            />
          </div>
          <div class="actions">
            <button class="btn btn-primary" @click="fetchEntries" :disabled="!canGenerate">{{$t('generate')}}</button>
            <div class="svg-circle-wrapper">
              <svgicon src="/icon/download.svg" :width="16" :height="16" @click="downloadAsCsv"/>
            </div>
          </div>
        </div>
      </div>

      <PanelCard v-if="(!filteredRows || filteredRows.length === 0) && !dates" size="big">
        <Empty smaller hideTitle :text="$t('timesheet.employee_card_empty')" :button="{text: $t('generate'), action: fetchEntries}"></Empty>
      </PanelCard>

      <PanelCard v-if="dates && filteredRows" class="report" size="big">
        <div class="filters">
          <input type="text" v-model.trim="search" :placeholder="$t('search') + '...'">
        </div>
        <div class="tables">
          <div class="table table--left">
            <div class="table-header">
              <div class="th icon"><!-- clock Glyph --></div>
              <div class="th name">{{$t('timesheet.employee')}}</div>
            </div>
            <div class="table-rows">
              <div class="tr" v-for="row of filteredRows" :key="`rl${row.employee.id}`">
                <div class="td icon">
                  <div class="icon-holder">
                    <svgicon src="/icon/timer.svg" :width="16" :height="16"/>
                  </div>
                </div>
                <div class="td name">{{fullName(row.employee)}}</div>
              </div>
            </div>
          </div>
          <div class="table table--inner">
            <table>
              <tr>
                <th v-for="date of dates">{{formatDate(date)}}</th>
              </tr>
              <tr v-for="row of filteredRows" :key="`ri${row.employee.id}`">
                <td v-for="date of dates" :key="`r${row.employee.id}d${date}`">{{minutesToDuration(row[date])}}</td>
              </tr>
            </table>
          </div>
          <div class="table table--right">
            <div class="table-header">
              <div class="th hours">{{$t('timesheet.total_hours')}}</div>
              <div class="th action"><!-- Action --></div>
            </div>
            <div class="table-rows">
              <div class="tr" v-for="row of filteredRows" :key="`rr${row.employee.id}`">
                <div class="td hours">{{minutesToDuration(row.total)}}</div>
                <div class="td action"><svgicon src="/icon/open.svg" :width="16" :height="16" @click="setEmployeeTimesheet(row.employee.id)"/></div>
              </div>
            </div>
          </div>
        </div>
        <div class="none" v-if="filteredRows.length === 0">{{$t("datatable.no_result")}}</div>
      </PanelCard>
    </div>

  </div>
</template>

<script>
import TimesheetEmployee from "@/components/qsr/TimesheetEmployee";
import Empty from "@/components/tools/Empty";
import moment from "moment";
import PanelCard from "@/components/PanelCard";
import {Core} from "@/core/Core";
import {debouncedComputedProperty} from "@/vueUtils";

const MOMENT_DATE_FORMAT = 'YYYY-MM-DD';
const MOMENT_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export default {
  name: "PunchReport",

  components: { PanelCard, TimesheetEmployee, Empty },

  data() {
    return {
      parameters: {
        start: null,
        end: null,
      },
      data: {
        start: null,
        end: null,
        entries: null,
        employees: null
      },
      timesheetWorkingData: {
        employee: null,
        entries: [],
        idsUnderModification: []
      },
    }
  },

  computed: {
    search: debouncedComputedProperty(500),

    company() {
      return this.$store.state.contextCompany;
    },

    companyId() {
      return this.company ? this.company.id : null;
    },

    canGenerate() {
      return this.company
          && this.company.id
          && this.parameters.start
          && this.parameters.end;
    },

    dates() {
      if (!this.data.start || !this.data.end) {
        return null;
      }
      let dates = [];
      for (let m = moment(this.data.start, MOMENT_DATE_FORMAT); m.isSameOrBefore(this.data.end); m.add(1, 'days')) {
        dates.push(m.format(MOMENT_DATE_FORMAT));
      }
      /* List<Date> */
      return dates;
    },

    aggregatedByEmployeeAndDate() {
      let aggregated = new Map();
      if (!this.data.employees || !this.data.entries) {
        return aggregated;
      }

      /* create a key for all employee */
      for (let employee of this.data.employees) {
        let employeeID = employee.id;
        if (!aggregated.has(employeeID)) {
          aggregated.set(employeeID, new Map());
        }
      }
      /* add entries by employee and date */
      for (let entry of this.data.entries) {
        if (!entry.start || !entry.end) {
          continue;
        }
        let employeeID = entry.employee.id;
        let inMinutes = this.diffInMinutes(entry.start, entry.end);
        let date = moment(entry.start, MOMENT_DATE_TIME_FORMAT).format(MOMENT_DATE_FORMAT);
        if (!aggregated.has(employeeID)) {
          aggregated.set(employeeID, new Map());
        }
        let byEmployee = aggregated.get(employeeID);
        if (!byEmployee.has(date)) {
          byEmployee.set(date, 0);
        }
        byEmployee.set(date, byEmployee.get(date) + inMinutes);

        /* add to employee total */
        if (!byEmployee.has('total')) {
          byEmployee.set('total', 0);
        }
        byEmployee.set('total', byEmployee.get('total') + inMinutes);
      }
      /* Map<EmployeeID, Map<Date|'total', Minutes>> */
      return aggregated;
    },

    rows() {
      if (!this.data.employees) {
        return null;
      }
      let rows = [];
      for (let [employeeID, mapByDate] of this.aggregatedByEmployeeAndDate.entries()) {
        let employee = this.data.employees.find(e => e.id === employeeID);
        if (employee) {
          let row = Object.fromEntries(mapByDate.entries());
          row.employee = employee;
          row.searchString = removeDiacritics(`${employee.firstName} ${employee.lastName} ${employee.publicName}`).toLowerCase();
          rows.push(row);
        }
      }
      /* List<{employee,total,searchString, ...dates}> */
      return rows;
    },

    filteredRows() {
      if (typeof this.search !== 'string' || !this.search || !Array.isArray(this.rows)) {
        return this.rows;
      }
      let searchFragments = removeDiacritics(this.search)
          .toLowerCase()
          .replace(/ +(?= )/g,'') /* remove multiple spaces */
          .split(' ') /* split by space */

      return this.rows.filter(row => searchFragments.every(f => row.searchString.includes(f)));
    }
  },

  watch: {
    company: {
      immediate: true,
      handler(company) {
        if (!company) {
          return;
        }
        let start = null;
        let end = null;
        if (company.settings && company.settings.timesheet && company.settings.timesheet.period) {
          let date = moment().format(MOMENT_DATE_FORMAT);
          let period = company.settings.timesheet.period;
          let refStart = company.settings.timesheet.refStart;
          let periodInfo = this.findPeriodInfoOfDate(date, period, refStart);
          start = periodInfo.start;
          end = periodInfo.end;
        }
        this.parameters.start = start || moment().startOf('week').format(MOMENT_DATE_FORMAT);
        this.parameters.end = end || moment().endOf('week').format(MOMENT_DATE_FORMAT);
        this.fetchEmployees(company.id)
      }
    }
  },

  methods: {
    async request(method, route, parameters) {
      showSpinner();
      let response = await Core.noThrowRequest(method, route, parameters);
      hideSpinner();
      return response;
    },

    async fetchEmployees(companyID) {
      if (!companyID) {
        return;
      }
      let response = await this.request('GET', '/api/employees', {company: companyID, augment: 'short'});
      if (response) {
        this.data.employees = response.data;
      }
    },

    async fetchEntries() {
      if (!this.canGenerate) {
        return;
      }
      let startDay = this.parameters.start.substring(0, 10);
      let endDay = this.parameters.end.substring(0, 10);
      let company = this.company.id;
      let closedOnly = true;
      let start = `${startDay} 00:00:00`;
      let end = `${endDay} 23:59:59`;
      let response = await this.request('GET', '/api/timesheet_entries', {company,start,end,closedOnly})
      if (response) {
        this.data.start = startDay;
        this.data.end = endDay;
        this.data.entries = response.data;
      }
    },

    downloadAsCsv() {
      if (!this.data.start || !this.data.end) {
        return;
      }
      let reformatDate = date => moment(date, MOMENT_DATE_FORMAT).format('YYMMDD');
      let fileName = `punch_${reformatDate(this.data.start)}_${reformatDate(this.data.end)}`;
      let columns = [
        this.$t('timesheet.employee'),
        ...this.dates.map(date => this.formatDate(date)),
        this.$t('timesheet.total_hours')
      ];
      let rows = this.filteredRows.map(row => ([
        this.fullName(row.employee),
        ...this.dates.map(date => this.minutesToDuration(row[date])),
        this.minutesToDuration(row.total)
      ]));
      _downloadAsCsv(fileName, [columns, ...rows]);
    },

    clearEmployeeTimesheet() {
      this.timesheetWorkingData.employee = null;
      this.timesheetWorkingData.entries = [];
      this.timesheetWorkingData.idsUnderModification = [];
    },

    setEmployeeTimesheet(employeeID = null) {
      let employee = this.data.employees.find(e => e.id === employeeID);
      if (employee) {
        let entries = this.data.entries.filter(e => e.employee.id === employeeID);
        this.timesheetWorkingData.employee = employee;
        this.timesheetWorkingData.entries = JSON.parse(JSON.stringify(entries));
        this.timesheetWorkingData.idsUnderModification = entries.map(e => e.id);
      }
    },

    async timesheetSave() {
      let entries = this.timesheetWorkingData.entries;
      let validation = this.hasTimeOverlap(entries);
      if (!validation.valid) {
        let conflict1 = validation.conflict[0];
        let conflict2 = validation.conflict[1];
        showAffirmation(this.$t("timesheet.punch_overlap_title"),
            this.$t("timesheet.punch_overlap_description").replace("{CONFLICT1}", conflict1).replace("{CONFLICT2}", conflict2));
        return;
      }
      let response = await this.request('POST', '/api/timesheet_entries', {entries: entries});
      if (response) {
        this.data.entries = this.data.entries
            .filter(e => !this.timesheetWorkingData.idsUnderModification.includes(e.id))
            .concat(response.entries)

        this.setEmployeeTimesheet(this.timesheetWorkingData.employee.id);
      }
    },

    hasTimeOverlap(entries) {
      if (entries.length === 1) {
        return {
          valid: true,
          conflict: []
        };
      }

      let sortedEntries = [...entries].sort((a, b) => a.start.localeCompare(b.start));
      let formatConflictRange = (entry) => {
        let prettyDateFormat = "L hh:mm A";
        let start = moment(entry.start, MOMENT_DATE_TIME_FORMAT);
        let end = moment(entry.end, MOMENT_DATE_TIME_FORMAT);
        return `${start.format(prettyDateFormat)} - ${end.format(prettyDateFormat)}`;
      };

      for (let i = 0; i < sortedEntries.length - 1; i++) {
        const currentEndTime = sortedEntries[i].end;
        const nextStartTime = sortedEntries[i + 1].start;
        if (currentEndTime > nextStartTime) {
          return {
            valid: false,
            conflict: [
              formatConflictRange(sortedEntries[i]),
              formatConflictRange(sortedEntries[i + 1])
            ]};
        }
      }

      return {
        valid: true,
        conflict: []
      };
    },

    timesheetQuit() {
      this.clearEmployeeTimesheet();
    },

    async timesheetSaveQuit() {
      await this.timesheetSave();
      this.timesheetQuit();
    },

    diffInMinutes(fromDateString, toDateString) {
      let fromMoment = moment(fromDateString, MOMENT_DATE_TIME_FORMAT);
      let toMoment = moment(toDateString, MOMENT_DATE_TIME_FORMAT);
      return toMoment.diff(fromMoment, 'minutes');
    },

    minutesToDuration(minutes) {
      if (!minutes) {
        return '00:00';
      }
      let hour = minutes > 0 ? Math.floor(minutes / 60) : Math.ceil(minutes / 60);
      let min = Math.abs(minutes % 60);
      return `${hour.toString().padStart(2,'0')}:${min.toString().padStart(2,'0')}`;
    },

    formatDate(date) {
      return moment(date, MOMENT_DATE_FORMAT).format('MMM. D')
    },

    shortName(e) {
      return `${e.publicName}`.trim() || `${e.firstName}`.trim();
    },

    fullName(e) {
      let fullName = `${e.lastName} ${e.firstName}`.trim();
      let publicName = typeof e.publicName === 'string' ? e.publicName.trim() : null;
      return publicName ? `${fullName} (${publicName})` : fullName;
    },

    findPeriodInfoOfDate(date, period, refStartDate) {
      let dateMoment = moment(date, MOMENT_DATE_FORMAT);
      let refMoment = moment(refStartDate, MOMENT_DATE_FORMAT);
      let start = null;
      let end = null;
      if (typeof period === 'string' && period.includes('week') && refMoment.isValid()) {
        let nbWeeks = parseInt(period.charAt(0)) || 1;
        let periodLengthIfDays = nbWeeks * 7;
        let dayOffsetWithPeriodStart = refMoment.isBefore(dateMoment)
            ? dateMoment.diff(refMoment, 'day') % periodLengthIfDays
            : periodLengthIfDays - (Math.abs(dateMoment.diff(refMoment, 'day')) % periodLengthIfDays);

        let dayOffsetWithPeriodEnd = periodLengthIfDays - dayOffsetWithPeriodStart - 1;
        start = moment(dateMoment).subtract(dayOffsetWithPeriodStart, 'days').format(MOMENT_DATE_FORMAT);
        end = moment(dateMoment).add(dayOffsetWithPeriodEnd, 'days').format(MOMENT_DATE_FORMAT);
      }
      return {start, end}
    }
  }
}
</script>

<style lang="scss" scoped>
.svgicon {
  cursor: pointer;
}
.svg-circle-wrapper {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
  border-radius: 50%;
  cursor: pointer;
  fill: #fc6650;
  position: relative;
  margin-left: 32px;
  &:before {
    content: "";
    display: block;
    position: absolute;
    left: -16px;
    height: 24px;
    width: 1px;
    background-color: #e6e6e6;
    margin-right: 16px;
  }
}
.footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 22px;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 16px;

  .title-block {
    :first-child {
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    :nth-child(2) {
      font-size: 14px;
      color: #808080;
    }
  }

  .parameters {
    display: flex;
    align-items: flex-end;
    height: 32px;
    .actions {
      display: flex;
      align-items: center;
    }
    label {
      margin-bottom: 5px;
      margin-left: 5px;
    }
    > :not(:last-child) {
      margin-right: 8px;
    }
  }
}
.tables {
  display: flex;

  .table-header, .tr {
    display: flex;
  }

  .table--left {
    flex-shrink: 0;
    .name {
      flex-grow: 1;
      white-space: nowrap;
    }

    .icon {
      padding: 8px 0 8px 16px;
      width: 48px;
    }

    .icon-holder {
      height: 32px;
      width: 32px;
      display: flex;
      border-radius: 6px;
      background-color: #f5f5f5;
      align-items: center;
      justify-content: center;
      fill: #ccc;
    }
  }

  .table--right {
    .th:last-child {
      width: 48px;
    }
    .hours {
      flex-grow: 1;
      text-align: right;
      font-weight: bold;
    }
  }

  .table--inner {
    flex-grow: 1;
    overflow-x: auto;

    &::-webkit-scrollbar {
      width: 100%;
      height: 18px;
    }

    table {
      width: 100%;
    }
  }

  th, .th {
    font-size: 14px;
    font-weight: bold;
    display: table-cell;
    padding: 0 16px 16px 16px;
    border-bottom: 1px solid #E6E6E6;
    white-space: nowrap;
    text-align: left;
  }

  td, .td {
    padding: 16px;
    display: table-cell;
    border-bottom: 1px solid #E6E6E6;
  }
}
.report {
  .filters {
    margin-bottom: 30px;
  }
}
.none {
  width: 100%;
  text-align: center;
  font-weight: bold;
  padding: 30px 0 10px 0;
}
</style>

<style lang="scss">
@media screen and (max-width: 1000px) {
  .punch-report {
    .td.icon, .th.icon {
      display: none;
    }
  }
  .punch-report .header {
    flex-direction: column;

    .title-block {
      width: 100%;
      margin-bottom: 20px;
    }

    .parameters {
      flex-direction: column;
      height: auto;
      width: 100%;

      & > div {
        margin-bottom: 10px;
      }

      .actions {
        margin-top: 10px;
      }

      .btn {
        flex-grow: 1;
      }

      & > div, .vdatetime, .vdatetime input {
        width: 100% !important;
        margin-right: 0 !important;
      }
    }
  }
}
</style>